import { renderGuideProduct, shopOrigins } from "./lib/public-guide";
import "./styles.css";
import { type GuideAccessoryRequirement, type GuideCatalogueProduct, type GuideFuel, type GuideInduction, type GuidePowerUnit } from "./lib/guide";
import { parseWorkbookBytes, sha256Hex } from "./lib/workbook";
import type { MatchRecommendation, ParsedWorkbook } from "./lib/types";

type View = "overview" | "import" | "inventory" | "match" | "sets" | "guide";
type MatchMode = "automatic" | "manual" | "outliers";

interface ReferenceSummary {
  injector_reference: string;
  total_count: number;
  available_count: number;
  assigned_count: number;
  latest_tested_at: string | null;
}

interface SetSummary {
  set_type: "flow_matched" | "non_flow_matched";
  id: string;
  injector_reference: string;
  quantity: number;
  customer_name: string;
  invoice_number: string;
  max_spread_percent: number | null;
  created_at: string;
  serial_numbers: string;
}

interface AvailableInjector {
  serial_number: string;
  latest_tested_at: string | null;
}

interface InjectorHistoryRow {
  test_run_id: string;
  source_row: number;
  result_type: string;
  repeat_number: number | null;
  tested_at: string | null;
  test_step: number | null;
  pressure: number | null;
  pressure_units: string | null;
  temperature: number | null;
  temperature_units: string | null;
  injector_voltage: number | null;
  rpm: number | null;
  duty_percent: number | null;
  engineering_units: string | null;
  fuel_factor: number | null;
  target_type: number | null;
  target: number | null;
  tolerance: number | null;
  machine_average: number | null;
  variation_percent: number | null;
  orifice_code: number | null;
  orifice_flow: number | null;
  injector_position: number;
  result_value: number;
  enabled: number;
  source_filename: string;
  imported_at: string;
  testplan_name: string | null;
  testplan_structure_version: string | null;
  testplan_data_version: string | null;
}

interface InjectorDetailData {
  injector: {
    injector_reference: string;
    serial_number: string;
    status: string;
    first_seen_at: string;
    latest_tested_at: string | null;
    deleted_at: string | null;
    set_id: string | null;
    customer_name: string | null;
    invoice_number: string | null;
    set_created_at: string | null;
    max_spread_percent: number | null;
    set_position: number | null;
  };
  latest: { serialNumber: string; values: Record<string, number> } | null;
  setComparison: MatchRecommendation | null;
  setup: Record<string, string | number | boolean | null> | null;
  history: InjectorHistoryRow[];
}

interface SetDetailData {
  set: {
    set_type: "flow_matched" | "non_flow_matched";
    id: string;
    injector_reference: string;
    quantity: number;
    customer_name: string;
    invoice_number: string;
    match_score: number | null;
    max_spread_percent: number | null;
    created_at: string;
  };
  injectors: Array<{ serial_number: string; set_position: number; values: Record<string, number> }>;
  recommendation: MatchRecommendation;
  testConditions: {
    tested_at: string | null;
    pressure: number | null;
    pressure_units: string | null;
    temperature: number | null;
    temperature_units: string | null;
    injector_voltage: number | null;
    rpm: number | null;
    engineering_units: string | null;
    testplan_name: string | null;
    source_filename: string;
  } | null;
}

interface DashboardData {
  inventory: { total: number; available: number; references_count: number };
  importCount: number;
  setCount: number;
  recentImports: Array<{
    source_filename: string;
    injector_reference: string;
    injector_count: number;
    result_row_count: number;
    imported_at: string;
  }>;
}

interface GuideIndexData {
  meta: { edition: string; author: string; imported_at: string } | null;
  makes: string[];
  applications: Array<{ id: number; make: string; display_name: string }>;
}

interface GuideRecommendationData {
  application: {
    id: number;
    make: string;
    vehicle: string;
    engine: string;
    displayName: string;
    groupName: string;
    groupNote: string;
    oeData: Array<{ label: string; value: string }>;
  };
  criteria: {
    induction: GuideInduction;
    fuel: GuideFuel;
    targetPower: number;
    inputUnit: "kw" | "hp";
    targetPowerKw: number;
    basePressureBar: number;
    boostBar: number;
    adjustmentPercent: number;
  };
  recommendations: Array<{
    id: number;
    flowCc: number;
    injectorSku: string;
    accessoryText: string;
    accessorySkus: string[];
    accessoryRequirements: GuideAccessoryRequirement[];
    note: string;
    maxPowerKw: number;
    powerUnit: GuidePowerUnit;
    adjustedMaxPowerKw: number;
    headroomKw: number;
    suitable: boolean;
    injectorProduct: GuideCatalogueProduct[];
    accessoryProducts: Record<string, GuideCatalogueProduct[]>;
  }>;
}

const state: {
  view: View;
  dashboard: DashboardData | null;
  references: ReferenceSummary[];
  sets: SetSummary[];
  parsedFiles: ParsedWorkbook[];
  parseErrors: string[];
  recommendation: MatchRecommendation | null;
  recommendationReference: string;
  matchMode: MatchMode;
  manualInjectors: AvailableInjector[];
  manualSelection: Set<string>;
  inventoryReference: string;
  inventoryInjectors: AvailableInjector[];
  injectorDetail: InjectorDetailData | null;
  setDetail: SetDetailData | null;
  detailLoading: boolean;
  loading: boolean;
  guideIndex: GuideIndexData | null;
  guideMake: string;
  guideApplicationId: string;
  guideInduction: GuideInduction;
  guideFuel: GuideFuel;
  guideTargetPower: string;
  guidePowerInputUnit: "kw" | "hp";
  guideBasePressureBar: string;
  guideBoostBar: string;
  guideResult: GuideRecommendationData | null;
} = {
  view: "overview",
  dashboard: null,
  references: [],
  sets: [],
  parsedFiles: [],
  parseErrors: [],
  recommendation: null,
  recommendationReference: "",
  matchMode: "automatic",
  manualInjectors: [],
  manualSelection: new Set<string>(),
  inventoryReference: "",
  inventoryInjectors: [],
  injectorDetail: null,
  setDetail: null,
  detailLoading: false,
  loading: false,
  guideIndex: null,
  guideMake: "",
  guideApplicationId: "",
  guideInduction: "forced_induction",
  guideFuel: "petrol",
  guideTargetPower: "",
  guidePowerInputUnit: "kw",
  guideBasePressureBar: "3",
  guideBoostBar: "0",
  guideResult: null,
};

const mount = document.querySelector<HTMLDivElement>("#app");
if (!mount) throw new Error("App mount element not found.");
const app: HTMLDivElement = mount;
let guideError = "";
const embeddedGuideMode = new URLSearchParams(window.location.search).get("embed") === "1";
const publicGuideMode = window.location.hostname === "injectors.spoolimports.com" || window.location.pathname.startsWith("/injector-guide");

const escapeHtml = (value: unknown): string =>
  String(value ?? "")
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#039;");

const formatDate = (value: string | null | undefined): string => {
  if (!value) return "—";
  const date = new Date(value.includes("T") || value.endsWith("Z") ? value : `${value.replace(" ", "T")}Z`);
  return Number.isNaN(date.valueOf())
    ? value
    : new Intl.DateTimeFormat("en-AU", { dateStyle: "medium", timeStyle: "short" }).format(date);
};

async function api<T>(path: string, options?: RequestInit): Promise<T> {
  const response = await fetch(path, {
    ...options,
    headers: { "Content-Type": "application/json", ...options?.headers },
  });
  const payload = await response.json() as { error?: string } & T;
  if (!response.ok) throw new Error(payload.error ?? "Request failed.");
  return payload;
}

function toast(message: string, kind: "success" | "error" = "success"): void {
  const element = document.createElement("div");
  element.className = `toast ${kind}`;
  element.textContent = message;
  document.body.appendChild(element);
  window.setTimeout(() => element.remove(), 4200);
}

const icons: Record<View, string> = {
  overview: '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M4 13h6V4H4v9Zm0 7h6v-4H4v4Zm10 0h6v-9h-6v9Zm0-16v4h6V4h-6Z"/></svg>',
  import: '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M11 16h2V8h3l-4-4-4 4h3v8Zm-5 4h12v-2H6v2Z"/></svg>',
  inventory: '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M4 5h16v4H4V5Zm0 5h16v9H4v-9Zm5 2v2h6v-2H9Z"/></svg>',
  match: '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="m16 3 2.3 2.3-4.6 4.6 1.4 1.4 4.6-4.6L22 9V3h-6ZM3 7h8v2H3V7Zm0 8h8v2H3v-2Zm0-4h11v2H3v-2Z"/></svg>',
  sets: '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M4 4h16v4H4V4Zm0 6h16v4H4v-4Zm0 6h16v4H4v-4Zm2-10v.01h.01V6H6Zm0 6v.01h.01V12H6Zm0 6v.01h.01V18H6Z"/></svg>',
  guide: '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M4 4h7v16H4V4Zm9 0h7v5h-7V4Zm0 7h7v9h-7v-9ZM6 7v2h3V7H6Zm0 4v2h3v-2H6Z"/></svg>',
};

function shell(content: string): string {
  const nav = (["overview", "import", "inventory", "match", "sets", "guide"] as View[]).map((view) => {
    const labels: Record<View, string> = { overview: "Overview", import: "Import tests", inventory: "Inventory", match: "Build a set", sets: "Matched sets", guide: "Application guide" };
    return `<button class="nav-item ${state.view === view ? "active" : ""}" data-view="${view}">${icons[view]}<span>${labels[view]}</span></button>`;
  }).join("");
  return `
    <div class="app-shell">
      <aside class="sidebar">
        <div class="brand"><img src="/spool-logo-black.png" alt="Spool Imports" /></div>
        <nav>${nav}</nav>
        <div class="sidebar-foot"><span class="status-dot"></span><span>Cloudflare connected</span></div>
      </aside>
      <main class="main"><header class="topbar"><div><p class="eyebrow">SPOOL IMPORTS · INJECTOR OPERATIONS</p><h1>${pageTitle()}</h1></div><div class="operator"><span>SI</span><div><strong>Workshop</strong><small>Internal system</small></div></div></header>${content}</main>
    </div>`;
}

function pageTitle(): string {
  return { overview: "Flow matching overview", import: "Import test results", inventory: "Injector inventory", match: "Build an injector set", sets: "Matched set register", guide: "Injector application guide" }[state.view];
}

function metric(label: string, value: number | string, note: string, accent = false): string {
  return `<article class="metric ${accent ? "accent" : ""}"><span>${escapeHtml(label)}</span><strong>${escapeHtml(value)}</strong><small>${escapeHtml(note)}</small></article>`;
}

const numberText = (value: number | null | undefined, digits = 1): string =>
  value === null || value === undefined ? "—" : value.toLocaleString("en-AU", { minimumFractionDigits: digits, maximumFractionDigits: digits });

const dutyFromSignature = (signature: string): number | null => {
  const match = signature.match(/([\d.]+)% duty/);
  return match ? Number(match[1]) : null;
};

const deviationPercent = (value: number, average: number): number => average === 0 ? 0 : ((value - average) / Math.abs(average)) * 100;

function flowConditions(recommendation: MatchRecommendation): MatchRecommendation["conditions"] {
  const candidates = recommendation.conditions
    .filter((condition) => condition.signature.toUpperCase().startsWith("FLOW") && dutyFromSignature(condition.signature) !== null)
    .sort((left, right) => (dutyFromSignature(left.signature) ?? 0) - (dutyFromSignature(right.signature) ?? 0));
  const preferred = [20, 80, 100]
    .map((duty) => candidates.find((condition) => dutyFromSignature(condition.signature) === duty))
    .filter((condition): condition is MatchRecommendation["conditions"][number] => Boolean(condition));
  return preferred.length >= 2 ? preferred : candidates.slice(0, 3);
}

function injectorFlowChart(detail: InjectorDetailData): string {
  const points = Object.entries(detail.latest?.values ?? {})
    .map(([signature, value]) => ({ signature, duty: dutyFromSignature(signature), value }))
    .filter((point): point is { signature: string; duty: number; value: number } => point.duty !== null)
    .sort((left, right) => left.duty - right.duty);
  if (points.length < 2) return '<div class="chart-empty">Not enough comparable duty-cycle readings to draw a flow curve.</div>';
  const comparison = new Map((detail.setComparison?.conditions ?? []).map((condition) => [condition.signature, condition.average]));
  const maximum = Math.max(...points.flatMap((point) => [point.value, comparison.get(point.signature) ?? point.value])) * 1.08;
  const minimumDuty = points[0]?.duty ?? 0;
  const maximumDuty = points.at(-1)?.duty ?? 100;
  const x = (duty: number): number => 66 + ((duty - minimumDuty) / Math.max(1, maximumDuty - minimumDuty)) * 536;
  const y = (value: number): number => 195 - (value / maximum) * 166;
  const path = points.map((point, index) => `${index ? "L" : "M"}${x(point.duty).toFixed(1)} ${y(point.value).toFixed(1)}`).join(" ");
  const comparisonPoints = points.filter((point) => comparison.has(point.signature));
  const comparisonPath = comparisonPoints.map((point, index) => `${index ? "L" : "M"}${x(point.duty).toFixed(1)} ${y(comparison.get(point.signature) ?? point.value).toFixed(1)}`).join(" ");
  return `<svg class="injector-flow-chart" viewBox="0 0 640 238" role="img" aria-label="Flow curve by duty cycle for injector ${escapeHtml(detail.injector.serial_number)}">
    <rect x="66" y="20" width="536" height="175" class="chart-frame" />
    ${[0, 0.5, 1].map((fraction) => `<line x1="66" y1="${(195 - fraction * 166).toFixed(1)}" x2="602" y2="${(195 - fraction * 166).toFixed(1)}" class="chart-grid" /><text x="57" y="${(199 - fraction * 166).toFixed(1)}" text-anchor="end">${numberText(maximum * fraction, 0)}</text>`).join("")}
    ${comparisonPath ? `<path d="${comparisonPath}" class="chart-set-line" />` : ""}
    <path d="${path}" class="chart-injector-line" />
    ${points.map((point) => `<g><circle cx="${x(point.duty).toFixed(1)}" cy="${y(point.value).toFixed(1)}" r="6" /><text x="${x(point.duty).toFixed(1)}" y="${(y(point.value) - 12).toFixed(1)}" text-anchor="middle" class="chart-value">${numberText(point.value)}</text><text x="${x(point.duty).toFixed(1)}" y="216" text-anchor="middle">${numberText(point.duty, 0)}%</text></g>`).join("")}
    <text x="334" y="235" text-anchor="middle" class="chart-axis-title">Duty cycle</text>
    <text x="14" y="108" transform="rotate(-90 14 108)" text-anchor="middle" class="chart-axis-title">Flow (${escapeHtml(detail.history[0]?.engineering_units ?? "units")})</text>
  </svg>`;
}

function injectorDetailOverlay(): string {
  if (!state.detailLoading && !state.injectorDetail) return "";
  if (state.detailLoading) return `<div class="overlay" role="dialog" aria-modal="true" aria-label="Loading injector details"><div class="detail-dialog"><button class="overlay-close" data-close-overlay aria-label="Close">×</button>${loadingBlock()}</div></div>`;
  const detail = state.injectorDetail;
  if (!detail) return "";
  const injector = detail.injector;
  const latestPoints = Object.entries(detail.latest?.values ?? {})
    .map(([signature, value]) => ({ signature, duty: dutyFromSignature(signature), value }))
    .filter((point) => point.duty !== null)
    .sort((left, right) => (left.duty ?? 0) - (right.duty ?? 0));
  const comparison = new Map((detail.setComparison?.conditions ?? []).map((condition) => [condition.signature, condition.average]));
  const latestRows = latestPoints.map((point) => {
    const average = comparison.get(point.signature);
    return `<tr><td>${numberText(point.duty, 0)}%</td><td>${numberText(point.value)}</td><td>${average === undefined ? "—" : `${deviationPercent(point.value, average) >= 0 ? "+" : ""}${numberText(deviationPercent(point.value, average), 2)}%`}</td></tr>`;
  }).join("");
  const historyRows = detail.history.map((row) => `<tr><td>${escapeHtml(formatDate(row.tested_at))}</td><td>${escapeHtml(row.result_type)}</td><td>${numberText(row.repeat_number, 0)}</td><td>${numberText(row.test_step, 0)}</td><td>${numberText(row.duty_percent, 0)}%</td><td><strong>${numberText(row.result_value)}</strong></td><td>${escapeHtml(row.engineering_units ?? "—")}</td><td>${numberText(row.pressure)} ${escapeHtml(row.pressure_units ?? "")}</td><td>${numberText(row.temperature)} ${escapeHtml(row.temperature_units ?? "")}</td><td>${numberText(row.injector_voltage)} V</td><td>${numberText(row.rpm, 0)}</td><td>${numberText(row.target)}</td><td>${numberText(row.tolerance)}</td><td>${numberText(row.machine_average)}</td><td>${numberText(row.variation_percent, 2)}%</td><td>${numberText(row.fuel_factor, 3)}</td><td>${numberText(row.orifice_code, 0)}</td><td>${numberText(row.orifice_flow)}</td><td>${row.enabled ? "Yes" : "No"}</td><td>${escapeHtml(row.testplan_name ?? "—")}</td><td>${escapeHtml(row.source_filename)}</td></tr>`).join("");
  const setupRows = Object.entries(detail.setup ?? {}).map(([key, value]) => `<div><dt>${escapeHtml(key)}</dt><dd>${escapeHtml(value)}</dd></div>`).join("");
  return `<div class="overlay" role="dialog" aria-modal="true" aria-labelledby="injector-detail-title"><div class="detail-dialog">
    <header class="detail-dialog-head"><div><p class="eyebrow">INJECTOR DETAIL</p><h2 id="injector-detail-title"><button class="serial-heading" type="button">${escapeHtml(injector.serial_number)}</button></h2><p>${escapeHtml(injector.injector_reference)} · ${injector.status === "assigned" ? `Assigned to position ${injector.set_position ?? "—"}` : "Available inventory"}</p></div><button class="overlay-close" data-close-overlay aria-label="Close injector detail">×</button></header>
    <div class="detail-metrics"><div><span>Status</span><strong>${injector.deleted_at ? "Deleted" : injector.status === "assigned" ? "Assigned" : "Available"}</strong></div><div><span>Latest test</span><strong>${escapeHtml(formatDate(injector.latest_tested_at))}</strong></div><div><span>Test records</span><strong>${detail.history.length}</strong></div><div><span>Set max spread</span><strong>${injector.max_spread_percent === null ? "—" : `${numberText(injector.max_spread_percent, 2)}%`}</strong></div></div>
    <div class="detail-columns"><section class="detail-primary"><div class="detail-section-head"><h3>Latest flow curve</h3><span>Injector${detail.setComparison ? " vs set average" : ""}</span></div>${injectorFlowChart(detail)}<div class="chart-legend"><span><i></i>Injector</span>${detail.setComparison ? "<span><i></i>Set average</span>" : ""}</div></section>
    <aside class="detail-aside"><section><h3>Latest measurements</h3><table class="compact-table"><thead><tr><th>Duty</th><th>Flow</th><th>vs set</th></tr></thead><tbody>${latestRows || emptyRow(3, "No duty-cycle readings")}</tbody></table></section><section><h3>Assignment</h3><dl class="detail-facts"><div><dt>Customer</dt><dd>${escapeHtml(injector.customer_name ?? "—")}</dd></div><div><dt>Invoice</dt><dd>${escapeHtml(injector.invoice_number ?? "—")}</dd></div><div><dt>Set created</dt><dd>${escapeHtml(formatDate(injector.set_created_at))}</dd></div><div><dt>First seen</dt><dd>${escapeHtml(formatDate(injector.first_seen_at))}</dd></div></dl>${injector.set_id ? `<button class="button secondary full" data-set-card="${escapeHtml(injector.set_id)}">View flow match card</button>` : ""}</section></aside></div>
    <details class="setup-details"><summary>Complete setup parameters</summary><dl class="setup-grid">${setupRows || "<div><dt>Setup</dt><dd>No setup data found</dd></div>"}</dl></details>
    <details class="history-details" open><summary>Complete test history and machine data</summary><div class="table-wrap"><table><thead><tr><th>Tested</th><th>Type</th><th>Repeat</th><th>Step</th><th>Duty</th><th>Result</th><th>Units</th><th>Pressure</th><th>Temp.</th><th>Voltage</th><th>RPM</th><th>Target</th><th>Tolerance</th><th>Machine avg.</th><th>Variation</th><th>Fuel factor</th><th>Orifice code</th><th>Orifice flow</th><th>Enabled</th><th>Test plan</th><th>Source file</th></tr></thead><tbody>${historyRows || emptyRow(21, "No test history found")}</tbody></table></div></details>
  </div></div>`;
}

function setCardOverlay(): string {
  const detail = state.setDetail;
  if (!detail) return "";
  const conditions = flowConditions(detail.recommendation);
  const nonMatched = detail.set.set_type === "non_flow_matched";
  const score = detail.recommendation.maxSpreadPercent;
  const units = detail.testConditions?.engineering_units ?? "cc/min";
  const extraRows = Math.max(0, Math.min(12, detail.injectors.length) - 8);
  const cardTableStyle = `--card-result-font: ${8 - extraRows * 0.25}px; --card-heading-font: ${9 - extraRows * 0.5}px; --card-row-padding: ${6 - extraRows * 0.75}px`;
  const memberRows = detail.injectors.map((injector) => {
    const deviations = conditions.map((condition) => deviationPercent(injector.values[condition.signature] ?? 0, condition.average));
    const worst = deviations.reduce((current, value) => Math.abs(value) > Math.abs(current) ? value : current, 0);
    return `<tr><td>${escapeHtml(injector.serial_number)}</td>${conditions.map((condition) => `<td>${numberText(injector.values[condition.signature])}</td>`).join("")}<td>${worst >= 0 ? "+" : ""}${numberText(worst, 2)}%</td></tr>`;
  }).join("");
  const averageRow = `<tr class="set-average-row"><td>SET AVERAGE</td>${conditions.map((condition) => `<td>${numberText(condition.average)}</td>`).join("")}<td>${numberText(score, 2)}% spread</td></tr>`;
  const rangeRows = conditions.map((condition) => `<div class="card-range-row"><strong>${numberText(dutyFromSignature(condition.signature), 0)}%</strong><span><i style="width:${Math.min(100, Math.max(8, condition.spreadPercent * 18))}%"></i><b></b></span><em>${numberText(condition.minimum, 0)}–${numberText(condition.maximum, 0)}</em></div>`).join("");
  return `<div class="overlay print-overlay" role="dialog" aria-modal="true" aria-labelledby="flow-card-title"><div class="print-dialog"><header class="print-toolbar"><div><p class="eyebrow">${nonMatched ? "CUSTOMER INJECTOR CARD" : "CUSTOMER FLOW MATCH CARD"}</p><h2 id="flow-card-title">${escapeHtml(detail.set.invoice_number)} · ${escapeHtml(detail.set.customer_name)}</h2><p>Print double-sided on 6 × 4 inch landscape stationery.</p></div><div><button class="button secondary" data-close-overlay>Close</button><button class="button primary" id="print-flow-card">Print card</button></div></header>
    <div class="print-pages">
      <article class="flow-card flow-card-front"><section class="card-identity"><img src="/spool-logo-black.png" alt="Spool Imports" /><div><small>${nonMatched ? "NON-FLOW-MATCHED" : "PRECISION MATCHED"}</small><h2>INJECTOR SET</h2><p>${nonMatched ? "Individually tested. Not dynamically flow matched." : "Individually tested. Dynamically matched."}</p><strong class="verified-mark"><span>✓</span> ${nonMatched ? "TESTED IN-HOUSE" : "VERIFIED IN-HOUSE"}</strong></div></section><section class="card-data"><div class="card-meta"><div><small>Injector reference</small><strong>${escapeHtml(detail.set.injector_reference)}</strong></div><div><small>Set quantity</small><strong>${detail.set.quantity} injectors</strong></div><div><small>Invoice</small><strong>${escapeHtml(detail.set.invoice_number)}</strong></div></div><div class="card-score"><div><h3>${nonMatched ? "Measured" : "Matched"} across ${conditions.length} flow points</h3><p>${nonMatched ? "Supplied as a non-flow-matched set. Spread is informational only." : "Every serial number was compared under controlled test conditions."}</p></div><div class="card-score-ring"><strong>${numberText(score, 2)}%</strong><small>MAX SPREAD</small></div></div><div class="card-ranges">${rangeRows}</div></section></article>
      <article class="flow-card flow-card-back" style="${cardTableStyle}"><header><div><h2>${nonMatched ? "Non-flow-matched test results" : "Certified flow results"}</h2><p>Measured in ${escapeHtml(units)} · latest comparable test cycle</p></div><img src="/spool-logo-white.png" alt="Spool Imports" /></header><div class="card-back-grid"><table><thead><tr><th>Serial number</th>${conditions.map((condition) => `<th>${numberText(dutyFromSignature(condition.signature), 0)}% duty</th>`).join("")}<th>Worst dev.</th></tr></thead><tbody>${memberRows}${averageRow}</tbody></table><aside><h3>Controlled test conditions</h3><dl><div><dt>Test plan</dt><dd>${escapeHtml(detail.testConditions?.testplan_name ?? "—")}</dd></div><div><dt>Pressure</dt><dd>${numberText(detail.testConditions?.pressure)} ${escapeHtml(detail.testConditions?.pressure_units ?? "")}</dd></div><div><dt>Voltage</dt><dd>${numberText(detail.testConditions?.injector_voltage)} V</dd></div><div><dt>Temperature</dt><dd>${numberText(detail.testConditions?.temperature)} ${escapeHtml(detail.testConditions?.temperature_units ?? "")}</dd></div><div><dt>RPM</dt><dd>${numberText(detail.testConditions?.rpm, 0)}</dd></div><div><dt>Tested</dt><dd>${escapeHtml(formatDate(detail.testConditions?.tested_at))}</dd></div><div><dt>Max spread</dt><dd>${numberText(score, 2)}%</dd></div></dl></aside></div><footer><strong>Spool Imports</strong><span>Unit 10, 18 Blanck St, Ormeau QLD 4208 · 07 5645 1333 · spoolimports.com</span></footer></article>
    </div></div></div>`;
}

function overlays(): string {
  return injectorDetailOverlay() + setCardOverlay();
}

function overviewView(): string {
  const data = state.dashboard;
  if (!data) return loadingBlock();
  const rows = data.recentImports.length
    ? data.recentImports.map((item) => `<tr><td><strong>${escapeHtml(item.source_filename)}</strong></td><td><span class="part-chip">${escapeHtml(item.injector_reference)}</span></td><td>${item.injector_count}</td><td>${item.result_row_count}</td><td>${escapeHtml(formatDate(item.imported_at))}</td></tr>`).join("")
    : emptyRow(5, "No test files have been imported yet.");
  const referenceCards = state.references.map((reference) => `
    <article class="inventory-card">
      <div><span class="part-chip dark">${escapeHtml(reference.injector_reference)}</span><small>Last tested ${escapeHtml(formatDate(reference.latest_tested_at))}</small></div>
      <div class="inventory-count"><strong>${reference.available_count}</strong><span>available</span></div>
      <div class="mini-track"><i style="width:${reference.total_count ? (reference.available_count / reference.total_count) * 100 : 0}%"></i></div>
      <footer>${reference.assigned_count} assigned · ${reference.total_count} total</footer>
    </article>`).join("") || '<div class="empty-card">Inventory appears here after the first import.</div>';

  return `<section class="content">
    <div class="metrics-grid">
      ${metric("Available injectors", data.inventory.available ?? 0, `${data.inventory.total ?? 0} tested in total`, true)}
      ${metric("Injector references", data.inventory.references_count ?? 0, "Part numbers in inventory")}
      ${metric("Test files", data.importCount, "Unique workbooks imported")}
      ${metric("Matched sets", data.setCount, "Confirmed customer sets")}
    </div>
    <div class="section-heading"><div><p class="eyebrow">LIVE INVENTORY</p><h2>Availability by injector reference</h2></div><div class="heading-actions"><button class="button secondary" data-view="inventory">Manage inventory</button><button class="button primary" data-view="match">Build a set</button></div></div>
    <div class="inventory-grid">${referenceCards}</div>
    <section class="panel"><div class="panel-heading"><div><p class="eyebrow">IMPORT HISTORY</p><h2>Recent test files</h2></div><button class="text-button" data-view="import">Import workbooks →</button></div>
      <div class="table-wrap"><table><thead><tr><th>File</th><th>Reference</th><th>Injectors</th><th>Tests</th><th>Imported</th></tr></thead><tbody>${rows}</tbody></table></div>
    </section>
  </section>`;
}

function importView(): string {
  const previews = state.parsedFiles.map((file) => `
    <article class="file-card">
      <div class="file-icon">X</div>
      <div class="file-detail"><strong>${escapeHtml(file.filename)}</strong><span>${escapeHtml(file.injectorReference)} · ${file.injectors.length} injectors · ${file.tests.length} result rows</span><small>${file.injectors.map((item) => escapeHtml(item.serialNumber)).join(" · ")}</small></div>
      <span class="valid-pill">Validated</span>
    </article>`).join("");
  const errors = state.parseErrors.map((error) => `<div class="inline-alert">${escapeHtml(error)}</div>`).join("");
  return `<section class="content narrow">
    <div class="intro"><p>Upload one or more ASNU result workbooks. The app reads injector serial numbers from <strong>SETUP</strong>, maps Injector 1–8 to <strong>RESULTS</strong>, and normalises every reading before import.</p></div>
    <label class="dropzone" id="dropzone">
      <input id="file-input" type="file" accept=".xlsx,.xls" multiple />
      <div class="upload-icon">↑</div><h2>Drop Excel test files here</h2><p>or click to select multiple files</p><span>ASNU .xlsx or .xls · up to 25 files at once</span>
    </label>
    ${errors}
    ${previews ? `<div class="file-list"><div class="section-heading compact"><h2>Ready to import</h2><button class="text-button" id="clear-files">Clear</button></div>${previews}<div class="import-actions"><p><strong>${state.parsedFiles.length}</strong> file${state.parsedFiles.length === 1 ? "" : "s"} · <strong>${state.parsedFiles.reduce((sum, file) => sum + file.injectors.length, 0)}</strong> injectors</p><button class="button primary" id="import-files" ${state.loading ? "disabled" : ""}>${state.loading ? "Importing…" : "Import validated files"}</button></div></div>` : ""}
  </section>`;
}

function referenceOptions(selected: string): string {
  return state.references.map((reference) => `<option value="${escapeHtml(reference.injector_reference)}" ${selected === reference.injector_reference ? "selected" : ""}>${escapeHtml(reference.injector_reference)} — ${reference.available_count} available</option>`).join("");
}

function inventoryView(): string {
  const rows = state.inventoryInjectors.length
    ? state.inventoryInjectors.map((injector) => `<tr><td><strong>${escapeHtml(injector.serial_number)}</strong></td><td>${escapeHtml(formatDate(injector.latest_tested_at))}</td><td><span class="status-pill">Available</span></td><td class="action-cell"><button class="button danger" data-delete-injector="${escapeHtml(injector.serial_number)}">Delete</button></td></tr>`).join("")
    : emptyRow(4, state.inventoryReference ? "No available injectors remain for this reference." : "Choose an injector reference to manage its inventory.");
  return `<section class="content"><section class="panel"><div class="panel-heading"><div><p class="eyebrow">INVENTORY MANAGEMENT</p><h2>Available injectors</h2><p class="panel-note">Deleting hides an injector and its results from matching. Assigned injectors cannot be deleted.</p></div><form id="inventory-filter" class="inline-form"><label>Injector reference<select name="reference" required><option value="">Choose a part number</option>${referenceOptions(state.inventoryReference)}</select></label><button class="button primary" type="submit" ${state.loading ? "disabled" : ""}>${state.loading ? "Loading…" : "Load inventory"}</button></form></div><div class="table-wrap"><table><thead><tr><th>Serial number</th><th>Latest test</th><th>Status</th><th>Action</th></tr></thead><tbody>${rows}</tbody></table></div></section></section>`;
}

function matchView(): string {
  const options = referenceOptions(state.recommendationReference);
  const recommendation = state.recommendation;
  const outliers = state.matchMode === "outliers";
  const result = recommendation ? `
    <section class="recommendation">
      <div class="match-summary"><div><p class="eyebrow">${outliers ? "NON-FLOW-MATCHED SET" : state.matchMode === "manual" ? "MANUALLY SELECTED GROUP" : "RECOMMENDED GROUP"}</p><h2>${recommendation.injectors.length} ${outliers ? "outlier-prioritised" : state.matchMode === "manual" ? "selected" : "closely matched"} injectors</h2><p>Compared across ${recommendation.sharedConditionCount} shared test conditions.${outliers ? ` Ranked from ${recommendation.eligibleCount} injectors in the largest identical test profile. This is a relative ranking, not a pass/fail test or a flow-match guarantee.` : ""}</p></div><div class="score-ring"><strong>${recommendation.maxSpreadPercent.toFixed(2)}%</strong><span>max spread</span></div></div>
      <div class="serial-grid">${recommendation.injectors.map((injector, index) => `<article><span>${String(index + 1).padStart(2, "0")}</span><div><small>Serial number</small><button class="serial-link" type="button" data-injector-detail="${escapeHtml(injector.serialNumber)}" data-injector-reference="${escapeHtml(state.recommendationReference)}">${escapeHtml(injector.serialNumber)}</button></div><i>${outliers ? `ISOLATION ${numberText(recommendation.outlierScores?.[injector.serialNumber], 2)}%` : "AVAILABLE"}</i></article>`).join("")}</div>
      <details><summary>Review condition spreads</summary><div class="table-wrap"><table><thead><tr><th>Condition</th><th>Minimum</th><th>Maximum</th><th>Average</th><th>Spread</th></tr></thead><tbody>${recommendation.conditions.map((condition) => `<tr><td>${escapeHtml(condition.signature)}</td><td>${condition.minimum.toFixed(1)}</td><td>${condition.maximum.toFixed(1)}</td><td>${condition.average.toFixed(1)}</td><td><strong>${condition.spreadPercent.toFixed(2)}%</strong></td></tr>`).join("")}</tbody></table></div></details>
      <form id="confirm-set" class="confirm-card"><div><p class="eyebrow">CUSTOMER ASSIGNMENT</p><h2>Confirm and reserve this set</h2><p>Confirmation reserves these serial numbers. You can undo the set from the matched set register.</p></div><label>Customer name<input name="customerName" required maxlength="160" placeholder="e.g. James Wilson" /></label><label>Invoice number<input name="invoiceNumber" required maxlength="100" placeholder="e.g. INV-10482" /></label><button class="button primary" type="submit" ${state.loading ? "disabled" : ""}>${state.loading ? "Confirming…" : outliers ? "Confirm non-flow-matched set" : "Confirm matched set"}</button></form>
    </section>` : state.matchMode === "automatic" ? `<div class="empty-state"><div class="empty-gauge">↗</div><h2>Ready to find the closest group</h2><p>Select a part number and quantity. The matcher compares the latest shared readings for every available injector.</p></div>` : "";

  const automaticBuilder = `<section class="match-builder"><div><p class="eyebrow">MATCH CRITERIA</p><h2>${outliers ? "Bundle available outliers" : "Select from available inventory"}</h2>${outliers ? "<p>Prioritises injectors furthest from their nearest potential set-mates, preserving tighter groups for flow-matched sales. Isolation is the average relative flow difference to the nearest quantity minus one injectors.</p>" : ""}</div><form id="match-form"><label>Injector reference<select name="reference" required><option value="">Choose a part number</option>${options}</select></label><label>Quantity<select name="quantity">${[2,3,4,5,6,7,8,9,10,11,12].map((number) => `<option value="${number}" ${number === 6 ? "selected" : ""}>${number} injectors</option>`).join("")}</select></label><button class="button primary" type="submit" ${state.loading ? "disabled" : ""}>${state.loading ? "Comparing…" : outliers ? "Find outlier set" : "Find closest match"}</button></form></section>`;

  const selectedCount = state.manualSelection.size;
  const manualCards = state.manualInjectors.map((injector) => {
    const selected = state.manualSelection.has(injector.serial_number);
    return `<article class="injector-choice ${selected ? "selected" : ""}"><label class="choice-select" aria-label="Select injector ${escapeHtml(injector.serial_number)}"><input type="checkbox" data-manual-injector="${escapeHtml(injector.serial_number)}" ${selected ? "checked" : ""} ${!selected && selectedCount >= 12 ? "disabled" : ""}/><span class="choice-check">✓</span></label><button class="injector-inspect" type="button" data-injector-detail="${escapeHtml(injector.serial_number)}" data-injector-reference="${escapeHtml(state.recommendationReference)}"><small>Serial number</small><strong>${escapeHtml(injector.serial_number)}</strong><em>Tested ${escapeHtml(formatDate(injector.latest_tested_at))} · tap to inspect</em></button></article>`;
  }).join("");
  const manualBuilder = `<section class="manual-builder"><form id="manual-reference-form" class="manual-reference"><div><p class="eyebrow">MANUAL SET</p><h2>Choose the exact injectors</h2><p>Select 2–12 available serial numbers, then review their measured spread before confirming.</p></div><label>Injector reference<select name="reference" required><option value="">Choose a part number</option>${options}</select></label><button class="button primary" type="submit" ${state.loading ? "disabled" : ""}>${state.loading ? "Loading…" : "Load injectors"}</button></form>${state.manualInjectors.length ? `<div class="manual-selection-bar"><strong>${selectedCount} selected</strong><span>Minimum 2 · maximum 12</span><button class="button primary" id="review-manual-set" ${selectedCount < 2 || state.loading ? "disabled" : ""}>${state.loading ? "Reviewing…" : "Review selected set"}</button></div><div class="injector-choice-grid">${manualCards}</div>` : `<div class="empty-state compact"><div class="empty-gauge">☝</div><h2>Select a reference to begin</h2><p>The available serial numbers will appear as touch-friendly selection cards.</p></div>`}</section>`;

  return `<section class="content"><div class="mode-switch" role="group" aria-label="Set creation method"><button class="${state.matchMode === "automatic" ? "active" : ""}" data-match-mode="automatic">Closest match</button><button class="${state.matchMode === "manual" ? "active" : ""}" data-match-mode="manual">Manual selection</button><button class="${outliers ? "active" : ""}" data-match-mode="outliers">Non-flow-matched</button></div>${state.matchMode !== "manual" ? automaticBuilder : manualBuilder}${result}</section>`;
}

function setsView(): string {
  const rows = state.sets.length ? state.sets.map((set) => `<tr><td><strong>${escapeHtml(set.invoice_number)}</strong><small>${escapeHtml(set.customer_name)}</small><small>${set.set_type === "non_flow_matched" ? "Non-flow-matched" : "Flow-matched"}</small></td><td><span class="part-chip">${escapeHtml(set.injector_reference)}</span></td><td>${set.quantity}</td><td class="serial-cell">${set.serial_numbers.split("|").map((serial) => `<button class="serial-link compact" type="button" data-injector-detail="${escapeHtml(serial)}" data-injector-reference="${escapeHtml(set.injector_reference)}">${escapeHtml(serial)}</button>`).join("")}</td><td>${set.max_spread_percent === null ? "—" : `${set.max_spread_percent.toFixed(2)}%`}</td><td>${escapeHtml(formatDate(set.created_at))}</td><td><button class="button secondary" type="button" data-set-card="${escapeHtml(set.id)}">${set.set_type === "non_flow_matched" ? "Test card" : "Flow card"}</button> <button class="button danger" type="button" data-undo-set="${escapeHtml(set.id)}" ${state.loading ? "disabled" : ""}>Undo set</button></td></tr>`).join("") : emptyRow(7, "No matched sets have been confirmed yet.");
  return `<section class="content"><section class="panel"><div class="panel-heading"><div><p class="eyebrow">ASSIGNMENT HISTORY</p><h2>Confirmed customer sets</h2></div><button class="button primary" data-view="match">Build a new set</button></div><div class="table-wrap"><table><thead><tr><th>Invoice / customer</th><th>Reference</th><th>Qty</th><th>Serial numbers</th><th>Max spread</th><th>Created</th><th>Actions</th></tr></thead><tbody>${rows}</tbody></table></div></section></section>`;
}

const guideApiBase = publicGuideMode ? "/api/public/guide" : "/api/guide";

const guideProduct = renderGuideProduct;

function guideAccessoryList(recommendation: GuideRecommendationData["recommendations"][number]): string {
  if (!recommendation.accessoryRequirements.length) {
    return `<div class="guide-direct-fit"><span>✓</span><div><strong>Direct fit</strong><small>No additional adaptors or accessories are listed for this option.</small></div></div>`;
  }
  return `<div class="guide-requirements">${recommendation.accessoryRequirements.map((requirement) => `
    <section><header><strong>${requirement.kind === "any_of" ? "Choose one of these" : "Required adapter or accessory"}</strong><small>${escapeHtml(requirement.label)}</small></header>
    <div>${requirement.skus.map((sku) => guideProduct(sku, recommendation.accessoryProducts[sku] ?? [], true)).join("")}</div></section>`).join("")}</div>`;
}

function guideResults(): string {
  const result = state.guideResult;
  if (!result) return `<section class="guide-empty"><span>1</span><h2>Choose the engine and target power</h2><p>The guide will match the application, induction type and fuel, then list the closest suitable injector and every required fitment item.</p></section>`;
  const criteria = result.criteria;
  const oe = result.application.oeData.map((item) => `<div><dt>${escapeHtml(item.label)}</dt><dd>${escapeHtml(item.value)}</dd></div>`).join("");
  if (!result.recommendations.length) {
    return `<section class="guide-results"><div class="guide-result-heading"><div><p class="eyebrow">${escapeHtml(result.application.make)}</p><h2>${escapeHtml(result.application.displayName)}</h2></div></div>
      <div class="guide-no-result"><h3>No suitable injector meets this target</h3><p>None of the listed injectors for this application, induction type and fuel can meet or exceed ${numberText(result.criteria.targetPowerKw, 0)} kW.</p><p>Check the setup with Spool Imports or the vehicle's tuner before selecting parts.</p></div>
      ${oe ? `<details class="guide-oe"><summary>Original injector details</summary><dl>${oe}</dl></details>` : ""}</section>`;
  }
  const powerUnit = result.recommendations[0]?.powerUnit ?? "RWKW";
  const cards = result.recommendations.map((recommendation, index) => {
    const status = "Minimum injector meeting target";
    const headroom = recommendation.headroomKw >= 0
      ? `${numberText(recommendation.headroomKw, 0)} kW headroom`
      : `${numberText(Math.abs(recommendation.headroomKw), 0)} kW below target`;
    return `<article class="guide-result-card ${recommendation.suitable ? "" : "warning"}">
      <div class="guide-card-top"><span class="guide-rank">${index + 1}</span><div><small>${status}</small><h3>${numberText(recommendation.flowCc, 0)} cc injector</h3></div><div class="guide-capacity"><strong>${numberText(recommendation.adjustedMaxPowerKw, 0)}</strong><span>${escapeHtml(powerUnit)}</span><small>${escapeHtml(headroom)}</small></div></div>
      <div class="guide-card-grid"><section><h4>Injector</h4>${guideProduct(recommendation.injectorSku, recommendation.injectorProduct)}</section><section><h4>Fitment parts</h4>${guideAccessoryList(recommendation)}</section></div>
      ${recommendation.note ? `<p class="guide-note"><strong>Important:</strong> ${escapeHtml(recommendation.note)}</p>` : ""}
    </article>`;
  }).join("");
  return `<section class="guide-results"><div class="guide-result-heading"><div><p class="eyebrow">${escapeHtml(result.application.make)}</p><h2>${escapeHtml(result.application.displayName)}</h2><p>${escapeHtml(result.application.groupName)}</p></div><div><small>Target</small><strong>${numberText(criteria.targetPowerKw, 0)} kW</strong><span>${escapeHtml(powerUnit)} basis</span></div></div>
    ${criteria.adjustmentPercent > 0 ? `<div class="guide-adjustment">The displayed capacity includes the guide's ${numberText(criteria.adjustmentPercent, 0)}% pressure adjustment for ${numberText(criteria.basePressureBar, 1)} bar base pressure${criteria.induction === "forced_induction" ? ` and ${numberText(criteria.boostBar, 1)} bar boost` : ""}.</div>` : ""}
    <div class="guide-result-list">${cards}</div>
    ${oe ? `<details class="guide-oe"><summary>Original injector and factory power details</summary><dl>${oe}</dl></details>` : ""}
    <p class="guide-disclaimer">Power figures are approximate limits from the supplied application guide and use a maximum 90% injector duty cycle. Confirm the complete fuel system and final injector choice with the vehicle's tuner.</p></section>`;
}

function guideForm(): string {
  const index = state.guideIndex;
  if (!index) return guideError ? `<section class="guide-error" role="alert"><p>${escapeHtml(guideError)}</p><button id="guide-retry" class="button primary" type="button">Try again</button></section>` : loadingBlock("Loading application guide…");
  const applications = index.applications.filter((application) => application.make === state.guideMake);
  const makeOptions = index.makes.map((make) => `<option value="${escapeHtml(make)}" ${state.guideMake === make ? "selected" : ""}>${escapeHtml(make)}</option>`).join("");
  const applicationOptions = applications.map((application) => `<option value="${application.id}" ${state.guideApplicationId === String(application.id) ? "selected" : ""}>${escapeHtml(application.display_name)}</option>`).join("");
  return `<form id="guide-form" class="guide-form">
    <div class="guide-form-heading"><p class="eyebrow">VEHICLE & ENGINE</p><h2>Build a recommendation</h2><p>Start with the exact application. Small engine and injector-style differences can change the required parts.</p></div>
    <label>Vehicle make<select id="guide-make" required><option value="">Choose make</option>${makeOptions}</select></label>
    <label>Engine application<select id="guide-application" required><option value="">Choose engine application</option>${applicationOptions}</select></label>
    <div class="guide-field-pair"><label>Induction<select id="guide-induction"><option value="naturally_aspirated" ${state.guideInduction === "naturally_aspirated" ? "selected" : ""}>Naturally aspirated</option><option value="forced_induction" ${state.guideInduction === "forced_induction" ? "selected" : ""}>Turbo or supercharged</option></select></label>
    <label>Fuel<select id="guide-fuel"><option value="petrol" ${state.guideFuel === "petrol" ? "selected" : ""}>Petrol</option><option value="e85" ${state.guideFuel === "e85" ? "selected" : ""}>E85</option><option value="methanol" ${state.guideFuel === "methanol" ? "selected" : ""}>Methanol</option></select></label></div>
    <div class="guide-power-field"><label>Target power at the driven wheels<input id="guide-power" type="number" min="1" max="10000" step="1" value="${escapeHtml(state.guideTargetPower)}" placeholder="e.g. 450" required /></label><label>Unit<select id="guide-power-unit"><option value="kw" ${state.guidePowerInputUnit === "kw" ? "selected" : ""}>kW</option><option value="hp" ${state.guidePowerInputUnit === "hp" ? "selected" : ""}>HP</option></select></label></div>
    <details class="guide-advanced"><summary>Advanced pressure adjustment</summary><p>The source guide rates injectors at 3 bar fixed fuel pressure and allows 10% more supported power for each additional bar of operating pressure.</p><div class="guide-field-pair"><label>Base fuel pressure (bar)<input id="guide-base-pressure" type="number" min="2" max="10" step="0.1" value="${escapeHtml(state.guideBasePressureBar)}" /></label><label>Boost pressure (bar)<input id="guide-boost" type="number" min="0" max="10" step="0.1" value="${escapeHtml(state.guideBoostBar)}" ${state.guideInduction === "naturally_aspirated" ? "disabled" : ""} /></label></div></details>
    <button class="button primary guide-submit" type="submit" ${state.loading ? "disabled" : ""}>${state.loading ? "Finding suitable parts…" : "Show recommended injectors"}</button>
    <small class="guide-source">Source: Injector Upgrade Guide ${escapeHtml(index.meta?.edition ?? "")} · ${index.applications.length} engine applications</small>
  </form>`;
}

function guideView(publicMode = false): string {
  const core = `<div class="guide-layout"><aside>${guideForm()}</aside><main id="guide-results" tabindex="-1" aria-busy="${state.loading}">${guideError && state.guideIndex ? `<p class="guide-error" role="alert">${escapeHtml(guideError)}</p>` : ""}${guideResults()}</main></div>`;
  if (publicMode && embeddedGuideMode) return `<div class="public-guide embedded-guide">${core}</div>`;
  if (!publicMode) return `<section class="content guide-content"><div class="guide-intro"><div><p class="eyebrow">APPLICATION & FITMENT</p><h2>Recommend the complete injector package</h2><p>Match the customer's engine, fuel and target power to the guide, including every adapter, cap, plug and O-ring SKU required for installation.</p></div></div>${core}</section>`;
  return `<div class="public-guide"><header><a href="https://spoolimports.com" aria-label="Spool Imports home"><img src="/spool-logo-black.png" alt="Spool Imports" /></a><div><span>Injector application guide</span><a href="https://spoolimports.com">Return to Spool Imports</a></div></header><section class="public-guide-hero"><p class="eyebrow">INJECTOR SELECTION</p><h1>Find the right injectors and fitment parts</h1><p>Select the exact engine, fuel and target power. The guide will show compatible injector options and the adapters or accessories needed to install them.</p></section>${core}<footer><strong>Spool Imports</strong><span>Injector and fitment guidance for performance applications</span></footer></div>`;
}

function emptyRow(columns: number, message: string): string {
  return `<tr><td colspan="${columns}" class="empty-row">${escapeHtml(message)}</td></tr>`;
}

function loadingBlock(message = "Loading flow data…"): string {
  return `<section class="content"><div class="loading"><i></i><span>${escapeHtml(message)}</span></div></section>`;
}

function render(): void {
  if (publicGuideMode) {
    document.title = "Injector Application Guide | Spool Imports";
    document.body.classList.add("public-guide-page");
    document.body.classList.toggle("embedded-guide-page", embeddedGuideMode);
    app.innerHTML = guideView(true);
    bindEvents();
    return;
  }
  const view = state.view === "overview" ? overviewView()
    : state.view === "import" ? importView()
      : state.view === "inventory" ? inventoryView()
        : state.view === "match" ? matchView()
          : state.view === "sets" ? setsView()
            : guideView();
  app.innerHTML = shell(view) + overlays();
  document.body.classList.toggle("overlay-open", state.detailLoading || Boolean(state.injectorDetail) || Boolean(state.setDetail));
  bindEvents();
}

function bindEvents(): void {
  document.querySelector("#guide-retry")?.addEventListener("click", () => {
    guideError = "";
    render();
    void loadGuideIndex().catch(() => { guideError = "We couldn’t load the injector selector. Please try again or contact Spool Imports for help."; }).finally(render);
  });
  document.querySelectorAll<HTMLElement>("[data-view]").forEach((element) => element.addEventListener("click", () => {
    state.view = element.dataset.view as View;
    state.recommendation = null;
    render();
  }));
  const fileInput = document.querySelector<HTMLInputElement>("#file-input");
  fileInput?.addEventListener("change", () => void processFiles(fileInput.files));
  const dropzone = document.querySelector<HTMLElement>("#dropzone");
  dropzone?.addEventListener("dragover", (event) => { event.preventDefault(); dropzone.classList.add("dragging"); });
  dropzone?.addEventListener("dragleave", () => dropzone.classList.remove("dragging"));
  dropzone?.addEventListener("drop", (event) => {
    event.preventDefault();
    dropzone.classList.remove("dragging");
    void processFiles(event.dataTransfer?.files ?? null);
  });
  document.querySelector("#clear-files")?.addEventListener("click", () => {
    state.parsedFiles = [];
    state.parseErrors = [];
    render();
  });
  document.querySelector("#import-files")?.addEventListener("click", () => void submitImports());
  document.querySelector<HTMLFormElement>("#inventory-filter")?.addEventListener("submit", (event) => void loadInventory(event));
  document.querySelectorAll<HTMLButtonElement>("[data-delete-injector]").forEach((button) => button.addEventListener("click", () => void removeInjector(button.dataset.deleteInjector ?? "")));
  document.querySelectorAll<HTMLButtonElement>("[data-match-mode]").forEach((button) => button.addEventListener("click", () => {
    if (state.loading) return;
    state.matchMode = button.dataset.matchMode as MatchMode;
    state.recommendation = null;
    state.manualSelection.clear();
    render();
  }));
  document.querySelector<HTMLFormElement>("#match-form")?.addEventListener("submit", (event) => void findMatch(event));
  document.querySelector<HTMLFormElement>("#manual-reference-form")?.addEventListener("submit", (event) => void loadManualInjectors(event));
  document.querySelectorAll<HTMLInputElement>("[data-manual-injector]").forEach((checkbox) => checkbox.addEventListener("change", () => {
    const serial = checkbox.dataset.manualInjector ?? "";
    if (checkbox.checked) state.manualSelection.add(serial); else state.manualSelection.delete(serial);
    state.recommendation = null;
    render();
  }));
  document.querySelector("#review-manual-set")?.addEventListener("click", () => void reviewManualSet());
  document.querySelector<HTMLFormElement>("#confirm-set")?.addEventListener("submit", (event) => void confirmSet(event));
  document.querySelectorAll<HTMLButtonElement>("[data-injector-detail]").forEach((button) => button.addEventListener("click", () => void openInjectorDetail(
    button.dataset.injectorReference ?? "",
    button.dataset.injectorDetail ?? "",
  )));
  document.querySelectorAll<HTMLButtonElement>("[data-undo-set]").forEach((button) => button.addEventListener("click", () => void undoMatchedSet(button.dataset.undoSet ?? "")));
  document.querySelectorAll<HTMLButtonElement>("[data-set-card]").forEach((button) => button.addEventListener("click", () => void openSetCard(button.dataset.setCard ?? "")));
  document.querySelectorAll<HTMLButtonElement>("[data-close-overlay]").forEach((button) => button.addEventListener("click", closeOverlays));
  document.querySelector("#print-flow-card")?.addEventListener("click", () => window.print());
  (document.querySelector("#guide-make") as HTMLSelectElement | null)?.addEventListener("change", (event) => {
    state.guideMake = (event.currentTarget as HTMLSelectElement).value;
    state.guideApplicationId = String(state.guideIndex?.applications.find((application) => application.make === state.guideMake)?.id ?? "");
    state.guideResult = null;
    render();
  });
  (document.querySelector("#guide-application") as HTMLSelectElement | null)?.addEventListener("change", (event) => {
    state.guideApplicationId = (event.currentTarget as HTMLSelectElement).value;
    state.guideResult = null;
  });
  (document.querySelector("#guide-induction") as HTMLSelectElement | null)?.addEventListener("change", (event) => {
    state.guideInduction = (event.currentTarget as HTMLSelectElement).value as GuideInduction;
    state.guideResult = null;
    render();
  });
  (document.querySelector("#guide-fuel") as HTMLSelectElement | null)?.addEventListener("change", (event) => {
    state.guideFuel = (event.currentTarget as HTMLSelectElement).value as GuideFuel;
    state.guideResult = null;
  });
  document.querySelector<HTMLInputElement>("#guide-power")?.addEventListener("input", (event) => { state.guideTargetPower = (event.currentTarget as HTMLInputElement).value; });
  (document.querySelector("#guide-power-unit") as HTMLSelectElement | null)?.addEventListener("change", (event) => { state.guidePowerInputUnit = (event.currentTarget as HTMLSelectElement).value as "kw" | "hp"; });
  document.querySelector<HTMLInputElement>("#guide-base-pressure")?.addEventListener("input", (event) => { state.guideBasePressureBar = (event.currentTarget as HTMLInputElement).value; });
  document.querySelector<HTMLInputElement>("#guide-boost")?.addEventListener("input", (event) => { state.guideBoostBar = (event.currentTarget as HTMLInputElement).value; });
  document.querySelector<HTMLFormElement>("#guide-form")?.addEventListener("submit", (event) => void submitGuide(event));
}

function closeOverlays(): void {
  state.injectorDetail = null;
  state.setDetail = null;
  state.detailLoading = false;
  render();
}

async function openInjectorDetail(reference: string, serialNumber: string): Promise<void> {
  if (!reference || !serialNumber) return;
  state.setDetail = null;
  state.injectorDetail = null;
  state.detailLoading = true;
  render();
  try {
    state.injectorDetail = await api<InjectorDetailData>(`/api/injector-detail?reference=${encodeURIComponent(reference)}&serial=${encodeURIComponent(serialNumber)}`);
  } catch (error) {
    toast(error instanceof Error ? error.message : "Could not load injector details.", "error");
  } finally {
    state.detailLoading = false;
    render();
  }
}

async function openSetCard(setId: string): Promise<void> {
  if (!setId) return;
  state.setDetail = null;
  state.detailLoading = true;
  render();
  try {
    state.setDetail = await api<SetDetailData>(`/api/sets/${encodeURIComponent(setId)}`);
  } catch (error) {
    toast(error instanceof Error ? error.message : "Could not load the flow match card.", "error");
  } finally {
    state.detailLoading = false;
    render();
  }
}

async function fetchAvailableInjectors(reference: string): Promise<AvailableInjector[]> {
  const result = await api<{ injectors: AvailableInjector[] }>(`/api/injectors?reference=${encodeURIComponent(reference)}`);
  return result.injectors;
}

async function loadInventory(event: SubmitEvent): Promise<void> {
  event.preventDefault();
  const form = new FormData(event.currentTarget as HTMLFormElement);
  const reference = String(form.get("reference") ?? "");
  if (!reference) return;
  state.loading = true;
  state.inventoryReference = reference;
  render();
  try {
    state.inventoryInjectors = await fetchAvailableInjectors(reference);
  } catch (error) {
    toast(error instanceof Error ? error.message : "Could not load inventory.", "error");
  } finally {
    state.loading = false;
    render();
  }
}

async function removeInjector(serialNumber: string): Promise<void> {
  if (!state.inventoryReference || !serialNumber) return;
  if (!window.confirm(`Delete injector ${serialNumber} from available inventory?\n\nIts historical test data will be retained.`)) return;
  state.loading = true;
  render();
  try {
    await api<{ serialNumber: string }>("/api/injectors", {
      method: "DELETE",
      body: JSON.stringify({ reference: state.inventoryReference, serialNumber }),
    });
    toast(`Injector ${serialNumber} deleted from inventory.`);
    await loadData();
    state.inventoryInjectors = await fetchAvailableInjectors(state.inventoryReference);
  } catch (error) {
    toast(error instanceof Error ? error.message : "Could not delete injector.", "error");
  } finally {
    state.loading = false;
    render();
  }
}

async function loadManualInjectors(event: SubmitEvent): Promise<void> {
  event.preventDefault();
  const form = new FormData(event.currentTarget as HTMLFormElement);
  const reference = String(form.get("reference") ?? "");
  if (!reference) return;
  state.loading = true;
  state.recommendation = null;
  state.recommendationReference = reference;
  state.manualInjectors = [];
  state.manualSelection.clear();
  render();
  try {
    state.manualInjectors = await fetchAvailableInjectors(reference);
  } catch (error) {
    toast(error instanceof Error ? error.message : "Could not load injectors.", "error");
  } finally {
    state.loading = false;
    render();
  }
}

async function reviewManualSet(): Promise<void> {
  const serialNumbers = Array.from(state.manualSelection);
  if (serialNumbers.length < 2 || serialNumbers.length > 12) return;
  state.loading = true;
  state.recommendation = null;
  render();
  try {
    const result = await api<{ recommendation: MatchRecommendation }>("/api/matches/manual-preview", {
      method: "POST",
      body: JSON.stringify({ reference: state.recommendationReference, serialNumbers }),
    });
    state.recommendation = result.recommendation;
  } catch (error) {
    toast(error instanceof Error ? error.message : "Could not review the selected set.", "error");
  } finally {
    state.loading = false;
    render();
  }
}

async function processFiles(files: FileList | null): Promise<void> {
  if (!files) return;
  const selected = Array.from(files).slice(0, 25);
  state.parseErrors = [];
  state.parsedFiles = [];
  state.loading = true;
  render();
  for (const file of selected) {
    try {
      if (!/\.xlsx?$/i.test(file.name)) throw new Error("Only .xlsx or .xls files are supported.");
      const bytes = await file.arrayBuffer();
      const sourceHash = await sha256Hex(bytes);
      state.parsedFiles.push(parseWorkbookBytes(bytes, file.name, sourceHash));
    } catch (error) {
      state.parseErrors.push(`${file.name}: ${error instanceof Error ? error.message : "Could not read workbook."}`);
    }
  }
  state.loading = false;
  render();
}

async function submitImports(): Promise<void> {
  state.loading = true;
  render();
  try {
    const result = await api<{ imported: Array<{ id: string }> }>("/api/imports", { method: "POST", body: JSON.stringify({ files: state.parsedFiles }) });
    toast(`${result.imported.length} test file${result.imported.length === 1 ? "" : "s"} imported.`);
    state.parsedFiles = [];
    await loadData();
    state.view = "overview";
  } catch (error) {
    toast(error instanceof Error ? error.message : "Import failed.", "error");
  } finally {
    state.loading = false;
    render();
  }
}

async function findMatch(event: SubmitEvent): Promise<void> {
  event.preventDefault();
  const form = new FormData(event.currentTarget as HTMLFormElement);
  const reference = String(form.get("reference") ?? "");
  if (!reference) return;
  state.loading = true;
  state.recommendation = null;
  state.recommendationReference = reference;
  render();
  try {
    const result = await api<{ recommendation: MatchRecommendation }>("/api/matches/preview", {
      method: "POST",
      body: JSON.stringify({ reference, quantity: Number(form.get("quantity")), setType: state.matchMode === "outliers" ? "non_flow_matched" : "flow_matched" }),
    });
    state.recommendation = result.recommendation;
  } catch (error) {
    toast(error instanceof Error ? error.message : "Could not find a match.", "error");
  } finally {
    state.loading = false;
    render();
  }
}

async function undoMatchedSet(setId: string): Promise<void> {
  if (state.loading) return;
  const set = state.sets.find((item) => item.id === setId);
  if (!set) return;
  if (!window.confirm(`Undo matched set for invoice ${set.invoice_number} (${set.customer_name})?\n\nAll ${set.quantity} injectors will return to available inventory and this set will be removed from the register. Test results will be retained.`)) return;
  state.loading = true;
  render();
  try {
    const result = await api<{ returnedCount: number }>(`/api/sets/${encodeURIComponent(setId)}`, { method: "DELETE" });
    state.sets = state.sets.filter((item) => item.id !== setId);
    state.recommendation = null;
    state.manualSelection.clear();
    state.manualInjectors = [];
    state.inventoryInjectors = [];
    state.setDetail = null;
    state.injectorDetail = null;
    toast(`Matched set undone. ${result.returnedCount} injectors returned to inventory.`);
    await loadData();
    if (state.inventoryReference) state.inventoryInjectors = await fetchAvailableInjectors(state.inventoryReference);
  } catch (error) {
    toast(error instanceof Error ? error.message : "Could not undo the matched set.", "error");
  } finally {
    state.loading = false;
    render();
  }
}

async function confirmSet(event: SubmitEvent): Promise<void> {
  event.preventDefault();
  if (!state.recommendation) return;
  const form = new FormData(event.currentTarget as HTMLFormElement);
  state.loading = true;
  render();
  try {
    await api<{ id: string }>("/api/sets", {
      method: "POST",
      body: JSON.stringify({
        setType: state.matchMode === "outliers" ? "non_flow_matched" : "flow_matched",
        reference: state.recommendationReference,
        serialNumbers: state.recommendation.injectors.map((injector) => injector.serialNumber),
        customerName: form.get("customerName"),
        invoiceNumber: form.get("invoiceNumber"),
        matchScore: state.recommendation.averageSpreadPercent,
        maxSpreadPercent: state.recommendation.maxSpreadPercent,
      }),
    });
    toast(state.matchMode === "outliers" ? "Non-flow-matched set confirmed and injectors reserved." : "Matched set confirmed and injectors reserved.");
    state.recommendation = null;
    state.manualInjectors = [];
    state.manualSelection.clear();
    await loadData();
    state.view = "sets";
  } catch (error) {
    toast(error instanceof Error ? error.message : "Could not confirm the set.", "error");
  } finally {
    state.loading = false;
    render();
  }
}

async function loadGuideIndex(): Promise<void> {
  const guide = await api<GuideIndexData>(guideApiBase);
  state.guideIndex = guide;
  if (!state.guideMake || !guide.makes.includes(state.guideMake)) state.guideMake = guide.makes[0] ?? "";
  const availableApplications = guide.applications.filter((application) => application.make === state.guideMake);
  if (!availableApplications.some((application) => String(application.id) === state.guideApplicationId)) {
    state.guideApplicationId = String(availableApplications[0]?.id ?? "");
  }
}

async function submitGuide(event: SubmitEvent): Promise<void> {
  event.preventDefault();
  if (!state.guideApplicationId || !state.guideTargetPower) return;
  state.loading = true;
  guideError = "";
  state.guideResult = null;
  render();
  try {
    state.guideResult = await api<GuideRecommendationData>(`${guideApiBase}/recommendations`, {
      method: "POST",
      body: JSON.stringify({
        applicationId: Number(state.guideApplicationId),
        induction: state.guideInduction,
        fuel: state.guideFuel,
        targetPower: Number(state.guideTargetPower),
        powerInputUnit: state.guidePowerInputUnit,
        basePressureBar: Number(state.guideBasePressureBar || 3),
        boostBar: Number(state.guideBoostBar || 0),
      }),
    });
  } catch (error) {
    if (publicGuideMode) guideError = "We couldn’t find your recommendation. Please check your selections and try again, or contact Spool Imports for help.";
    else toast(error instanceof Error ? error.message : "Could not build an injector recommendation.", "error");
  } finally {
    state.loading = false;
    render();
    if (publicGuideMode && state.guideResult) document.querySelector<HTMLElement>("#guide-results")?.focus({ preventScroll: true });
  }
}

async function loadData(): Promise<void> {
  try {
    const [dashboard, references, sets, guide] = await Promise.all([
      api<DashboardData>("/api/dashboard"),
      api<{ references: ReferenceSummary[] }>("/api/references"),
      api<{ sets: SetSummary[] }>("/api/sets"),
      api<GuideIndexData>(guideApiBase),
    ]);
    state.dashboard = dashboard;
    state.references = references.references;
    state.sets = sets.sets;
    state.guideIndex = guide;
    if (!state.guideMake) state.guideMake = guide.makes[0] ?? "";
    if (!state.guideApplicationId) state.guideApplicationId = String(guide.applications.find((application) => application.make === state.guideMake)?.id ?? "");
  } catch (error) {
    toast(error instanceof Error ? error.message : "Could not load application data.", "error");
  }
}

render();
if (publicGuideMode) {
  void loadGuideIndex().catch(() => { guideError = "We couldn’t load the injector selector. Please try again or contact Spool Imports for help."; }).finally(render);
} else {
  void loadData().finally(render);
}

// Only the two store origins can receive size updates; no customer or inventory data is sent.
if (publicGuideMode && embeddedGuideMode && window.parent !== window) {
  let lastHeight = 0;
  const reportSize = () => {
    const height = Math.ceil(app.getBoundingClientRect().height);
    if (height === lastHeight) return;
    lastHeight = height;
    for (const origin of shopOrigins) window.parent.postMessage({ type: "spool-injector-selector:resize", height }, origin);
  };
  new ResizeObserver(reportSize).observe(app);
  window.addEventListener("message", (event) => {
    if (event.source === window.parent && shopOrigins.includes(event.origin) && event.data?.type === "spool-injector-selector:measure") {
      lastHeight = 0;
      reportSize();
    }
  });
}
